/**
 * The pricing evaluator, mirrored from `ingest/src/bulbee_ingest/ratemodel.py`.
 *
 * Both implementations run the golden cases in `fixtures/pricing-cases.json`,
 * so if this drifts from the Python one a test fails rather than users being
 * quoted a different price than the ingest pipeline validated.
 *
 * Bill formula:
 *
 *   bill(u) = base_charge
 *           + tdu_monthly
 *           + energy_cost(u)
 *           + u * tdu_cents_per_kwh / 100
 *           - bill_credits(u)
 *           + minimum_usage_fee(u)
 *
 *   avg_cents_per_kwh(u) = bill(u) / u * 100
 */

export type EnergyChargeKind = "flat" | "tiered" | "tou";

/**
 * How a tiered energy charge applies.
 *
 * `marginal`  - only the kWh inside a band are priced at that band's rate.
 * `all_units` - the band containing total usage sets one rate for every kWh.
 *               Common in Texas EFLs, and it produces cost cliffs at the edges.
 */
export type TierMode = "marginal" | "all_units";

export type Confidence = "high" | "medium" | "low" | "unparsed";

export interface Tier {
  from_kwh: number;
  to_kwh: number | null;
  cents_per_kwh: number;
}

export interface EnergyCharge {
  kind?: EnergyChargeKind;
  cents_per_kwh?: number;
  tiers?: Tier[];
  tier_mode?: TierMode;
}

export interface BillCredit {
  min_kwh: number;
  max_kwh: number | null;
  amount_usd: number;
}

export interface MinimumUsageFee {
  below_kwh: number;
  amount_usd: number;
}

export interface TduCharges {
  monthly_usd?: number;
  cents_per_kwh?: number;
}

export interface RateStructure {
  base_charge_monthly_usd?: number;
  energy_charge?: EnergyCharge;
  tdu_charges_bundled?: boolean;
  bill_credits?: BillCredit[];
  minimum_usage_fee?: MinimumUsageFee | null;
}

export interface BillBreakdown {
  usage_kwh: number;
  base_charge_usd: number;
  energy_usd: number;
  tdu_fixed_usd: number;
  tdu_variable_usd: number;
  bill_credit_usd: number;
  minimum_usage_fee_usd: number;
  total_usd: number;
  avg_cents_per_kwh: number;
}

export function energyCost(charge: EnergyCharge | undefined, usageKwh: number): number {
  const spec = charge ?? {};
  const kind = spec.kind ?? "flat";
  const flatRate = spec.cents_per_kwh ?? 0;

  if (kind !== "tiered") {
    // A time-of-use plan has no honest single-rate answer; the ingest pipeline
    // records the blended rate it found and flags the plan for the UI.
    return (usageKwh * flatRate) / 100;
  }

  const tiers = spec.tiers ?? [];
  if (tiers.length === 0) {
    return (usageKwh * flatRate) / 100;
  }

  const ordered = [...tiers].sort((a, b) => a.from_kwh - b.from_kwh);
  if ((spec.tier_mode ?? "all_units") === "all_units") {
    let rate = ordered[ordered.length - 1].cents_per_kwh;
    for (const tier of ordered) {
      const upper = tier.to_kwh ?? Number.POSITIVE_INFINITY;
      if (usageKwh >= tier.from_kwh && usageKwh < upper) {
        rate = tier.cents_per_kwh;
        break;
      }
    }
    return (usageKwh * rate) / 100;
  }

  let total = 0;
  for (const tier of ordered) {
    const upper = tier.to_kwh ?? Number.POSITIVE_INFINITY;
    const band = Math.min(usageKwh, upper) - tier.from_kwh;
    if (band > 0) {
      total += (band * tier.cents_per_kwh) / 100;
    }
  }
  return total;
}

function creditApplies(credit: BillCredit, usageKwh: number): boolean {
  if (usageKwh < credit.min_kwh) return false;
  return credit.max_kwh === null || credit.max_kwh === undefined || usageKwh <= credit.max_kwh;
}

export function computeBill(
  rate: RateStructure,
  tdu: TduCharges,
  usageKwh: number,
): BillBreakdown {
  if (!(usageKwh > 0)) {
    throw new Error("usageKwh must be positive");
  }

  const baseCharge = rate.base_charge_monthly_usd ?? 0;
  const energy = energyCost(rate.energy_charge, usageKwh);

  const bundled = rate.tdu_charges_bundled ?? false;
  const tduFixed = bundled ? 0 : (tdu.monthly_usd ?? 0);
  const tduVariable = bundled ? 0 : (usageKwh * (tdu.cents_per_kwh ?? 0)) / 100;

  // Qualifying credits stack: an unconditional autopay credit can sit alongside
  // a usage credit, and "additional" credits at higher tiers apply on top.
  let credit = 0;
  for (const c of rate.bill_credits ?? []) {
    if (creditApplies(c, usageKwh)) credit += c.amount_usd;
  }

  const fee =
    rate.minimum_usage_fee && usageKwh < rate.minimum_usage_fee.below_kwh
      ? rate.minimum_usage_fee.amount_usd
      : 0;

  // A credit larger than the bill zeroes it out; retailers do not send refunds.
  const total = Math.max(baseCharge + tduFixed + energy + tduVariable - credit + fee, 0);

  return {
    usage_kwh: usageKwh,
    base_charge_usd: baseCharge,
    energy_usd: energy,
    tdu_fixed_usd: tduFixed,
    tdu_variable_usd: tduVariable,
    bill_credit_usd: credit,
    minimum_usage_fee_usd: fee,
    total_usd: total,
    avg_cents_per_kwh: (total / usageKwh) * 100,
  };
}

export function averagePriceCents(
  rate: RateStructure,
  tdu: TduCharges,
  usageKwh: number,
): number {
  return computeBill(rate, tdu, usageKwh).avg_cents_per_kwh;
}
